import { PanelLeft, ChevronRight, ChevronDown, XCircle, Check, Maximize, Minimize } from 'lucide-react';
import React from 'react';
import { useSidebar } from '@/components/ui/sidebar';
import { useLocation, Link } from 'react-router';
import { buildUrlWithParams } from '@/modules/form-shared-resources/utils/helper';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { DateRangeFilter } from '@/modules/shared/blocks/Filters/DateRangeFilter';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { usePipelineStore, getDefaultDateRange } from '@/modules/voluntary-insurance/shared/store/usePipelineStore';
import { isDefaultDateRange } from '@/modules/voluntary-insurance/shared/store/pipelinePersist';
import { useStaffList } from '@/modules/staff-management/shared/queries/useStaffList';
import { toStaffOption } from '@/modules/staff-management/shared/staffOptions';
import { MAX_DATE_RANGE_MONTHS } from '@/modules/voluntary-insurance/constants';
import { useFullscreen } from '@/modules/shared/hooks/useFullscreen';
import { trackEvent } from '@/modules/shared/analytics';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { en as sharedEn, th as sharedTh } from '@/modules/shared/i18n/locales';
import { en as navEn, th as navTh } from '@/components/main-navigation/locales';
import { en as headerEn, th as headerTh } from './locales';
import { formatDate } from '@/modules/shared/utils';
import { useBreadcrumbStore } from '@/modules/shared/store/useBreadcrumbStore';

interface BreadcrumbEntry {
  label: string;
  path?: string;
}

function AuthenticatedHeader() {
  const { pathname } = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const { t } = useTranslation({
    en: { ...sharedEn, ...navEn, ...headerEn },
    th: { ...sharedTh, ...navTh, ...headerTh },
  });

  const isCollapsed = state === 'collapsed';
  const { isFullscreen, isSupported: isFullscreenSupported, toggle: toggleFullscreen } = useFullscreen();

  // `isFullscreen` here is the pre-toggle state, so it describes what the click is asking for.
  function handleFullscreenClick() {
    trackEvent('fullscreen_clicked', {
      parent: 'Authenticated Header',
      action: isFullscreen ? 'exit' : 'enter',
    });
    void toggleFullscreen();
  }

  // Breadcrumb configuration for different routes
  // preserveParams: true = preserve existing URL parameters, false = clean navigation
  const breadcrumbConfig: Record<string, BreadcrumbEntry[]> = {
    '/': [{ label: t('createLeads'), path: '/' }],
    '/create-leads': [{ label: t('createLeads'), path: '/create-leads' }],
    '/buy-insurance': [{ label: t('buyInsurance'), path: '/buy-insurance' }],
    '/get-quote/voluntary-insurance': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
    ],
    '/get-quote/non-motor': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('nonMotor'), path: `${buildUrlWithParams('/get-quote/non-motor')}` },
    ],
    '/get-quote/change-of-agent': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('changeOfAgent'), path: `${buildUrlWithParams('/get-quote/change-of-agent')}` },
    ],
    '/plan-listing': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/plan-listing')}` },
    ],
    '/non-motor-plan-listing': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('nonMotor'), path: `${buildUrlWithParams('/get-quote/non-motor')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/non-motor-plan-listing')}` },
    ],
    '/get-quote/custom-quote': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/plan-listing')}` },
      { label: t('requestCustomQuote'), path: `${buildUrlWithParams('/get-quote/custom-quote')}` },
    ],
    '/sale-reporting': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/plan-listing')}` },
      { label: t('reportSale'), path: `${buildUrlWithParams('/sale-reporting')}` },
    ],
    '/send-to-customer': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/plan-listing')}` },
      { label: t('sendToCustomer'), path: `${buildUrlWithParams('/send-to-customer')}` },
    ],
    '/comparison-sheet': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('vmi'), path: `${buildUrlWithParams('/get-quote/voluntary-insurance')}` },
      { label: t('selectPlans'), path: `${buildUrlWithParams('/plan-listing')}` },
      { label: t('sendToCustomer'), path: `${buildUrlWithParams('/send-to-customer')}` },
      { label: t('quotations'), path: `${buildUrlWithParams('/comparison-sheet')}` },
    ],
    '/agents/all-agents': [{ label: t('allAgents'), path: '/agents/all-agents' }],
    '/agents/mlm-agents': [{ label: t('mlmAgents'), path: '/agents/mlm-agents' }],
    '/agents/cmi-subagents': [{ label: t('cmiSubagents'), path: '/agents/cmi-subagents' }],
    '/agents/cmi-subagents/commissions': [
      { label: t('cmiSubagents'), path: '/agents/cmi-subagents' },
      { label: t('commissionRates'), path: '/agents/cmi-subagents/commissions' },
    ],
    '/teams': [
      { label: t('mlmAgents'), path: '/agents/mlm-agents' },
      { label: t('teams'), path: '/teams' },
    ],
    '/leads/new-leads': [{ label: t('newLeads'), path: '/leads/new-leads' }],
    '/leads/coa': [{ label: t('changeOfAgent'), path: '/leads/coa' }],
    '/leads/renewals': [{ label: t('renewals'), path: '/leads/renewals' }],
    '/policy-in-progress/full-payment': [{ label: t('fullPayment'), path: '/policy-in-progress/full-payment' }],
    '/policy-in-progress/installment': [{ label: t('instalments'), path: '/policy-in-progress/installment' }],
    '/compulsory-insurance': [{ label: t('compulsoryInsurance'), path: '/policy-in-progress/compulsory-insurance' }],
    '/endorsements': [{ label: t('endorsements'), path: '/policy-in-progress/endorsements' }],
    '/crm': [{ label: t('crm'), path: '/crm' }],
    '/crm/debt-collection': [{ label: t('debtCollectionCRM'), path: '/crm/debt-collection' }],
    '/profile': [{ label: t('profile'), path: '/profile' }],
    '/accounting/agents/pending-payments': [
      { label: t('pendingPayments'), path: '/accounting/agents/pending-payments' },
    ],
    '/accounting/agents/upload-transfer-csv': [
      { label: t('uploadTransferCsv'), path: '/accounting/agents/upload-transfer-csv' },
    ],
    '/accounting/agents/combined-payments': [
      { label: t('combinedPayments'), path: '/accounting/agents/combined-payments' },
    ],
    '/accounting/agents/combined-endorsement': [
      { label: t('combinedEndorsement'), path: '/accounting/agents/combined-endorsement' },
    ],
    '/accounting/agents/upload-csv-endorsement': [
      { label: t('uploadCsvEndorsement'), path: '/accounting/agents/upload-csv-endorsement' },
    ],
    '/accounting/insurer-tables/soa-v1': [{ label: t('soaV1'), path: '/accounting/insurer-tables/soa-v1' }],
    '/accounting/insurer-tables/soa-results': [
      { label: t('soaResults'), path: '/accounting/insurer-tables/soa-results' },
    ],
    '/accounting/instalments/manual-ekyc-transfer': [
      { label: t('manualEkycTransfer'), path: '/accounting/instalments/manual-ekyc-transfer' },
    ],
    '/accounting/credits/credit-invoices': [
      { label: t('creditInvoices'), path: '/accounting/credits/credit-invoices' },
    ],
    '/accounting/credits/voluntary-insurance': [
      { label: t('voluntaryInsurance'), path: '/accounting/credits/voluntary-insurance' },
    ],
    '/accounting/credits/compulsory-insurance': [
      { label: t('compulsoryInsurance'), path: '/accounting/credits/compulsory-insurance' },
    ],
    '/accounting/credits/travel': [{ label: t('travel'), path: '/accounting/credits/travel' }],
    '/accounting/credits/all-products': [{ label: t('allProducts'), path: '/accounting/credits/all-products' }],
    '/accounting/upload-wht': [{ label: t('uploadWHTCertificate'), path: '/accounting/upload-wht' }],
    '/gamification-admin': [{ label: t('gamificationAdmin'), path: '/gamification-admin' }],
    '/ir-settings/pre-quote-sla': [{ label: t('preQuoteSLA'), path: '/ir-settings/pre-quote-sla' }],
    '/ir-settings/pre-sale-sla': [{ label: t('preSaleSLA'), path: '/ir-settings/pre-sale-sla' }],
    '/ir-settings/coa-form': [{ label: t('coaForm'), path: '/ir-settings/coa-form' }],
    '/ir-settings/insurer': [{ label: t('insurer'), path: '/ir-settings/insurer' }],
    '/ir-settings/insurer-tier': [{ label: t('insurerTier'), path: '/ir-settings/insurer-tier' }],
    '/ir-settings/affiliate-specific-commission-rates': [
      { label: t('affiliateSpecificCommissionRates'), path: '/ir-settings/affiliate-specific-commission-rates' },
    ],
    '/non-standard-leads': [{ label: t('nonStandardLeads'), path: '/non-standard-leads' }],
    '/non-standard-in-progress': [{ label: t('nonStandardPolicies'), path: '/non-standard-in-progress' }],
    '/non-motor-leads': [{ label: t('nonMotor'), path: '/non-motor-leads' }],
    '/staff-for-tasks': [{ label: t('staffForTask'), path: '/staff-for-tasks' }],
    '/affiliate-dashboard': [{ label: t('affiliateDashboard'), path: '/affiliate-dashboard' }],
    '/event-calendar': [{ label: t('eventCalendar'), path: '/event-calendar' }],
    '/historical-report-requests': [{ label: t('historicalReportRequests'), path: '/historical-report-requests' }],
    '/holidays': [{ label: t('holidays'), path: '/holidays' }],
    '/affiliates': [{ label: t('customerSupport'), path: '/affiliates' }],
    '/price-list-admin/*': [{ label: t('priceListAdmin'), path: '/price-list-admin' }],
    '/insurer-settings/*': [{ label: t('insurerSetting'), path: '/insurer-settings' }],
    '/promotion-admin/package-promotion': [
      { label: t('packagePromotion'), path: '/promotion-admin/package-promotion' },
    ],
    '/promotion-admin/campaign-promotion/upload': [
      { label: t('campaignPromotion'), path: '/promotion-admin/campaign-promotion' },
      { label: t('upload'), path: '/promotion-admin/campaign-promotion/upload' },
    ],
    '/promotion-admin/campaign-promotion/inventory': [
      { label: t('campaignPromotion'), path: '/promotion-admin/campaign-promotion' },
      { label: t('inventory'), path: '/promotion-admin/campaign-promotion/inventory' },
    ],
    '/policy-admin': [{ label: t('policyAdmin'), path: '/policy-admin' }],
    '/voluntary-insurance/*': [
      { label: t('motorPolicy'), path: '/voluntary-insurance' },
      { label: t('voluntaryInsurance'), path: '/voluntary-insurance' },
    ],
    '/assignment-config': [{ label: t('assignmentConfig'), path: '/assignment-config' }],
    '/endorsement': [{ label: t('endorsement'), path: '/endorsement' }],
    '/commission-and-promotions/agent-transfers': [
      { label: t('agentTransfers'), path: '/commission-and-promotions/agent-transfers' },
    ],
    // Sale-update journeys are multi-stage, but each is one destination as far as the header is
    // concerned — a single flow label rather than a crumb per stage.
    '/record-endorsement/*': [{ label: t('recordEndorsement'), path: '/record-endorsement/endorsement-details' }],
    '/cancellation-endorsement/*': [
      { label: t('cancellationEndorsement'), path: '/cancellation-endorsement/cancellation-details' },
    ],
    '/switch-package/*': [{ label: t('switchPackage'), path: '/switch-package/information-correction' }],
    '/premium-update': [{ label: t('updatePremium'), path: '/premium-update' }],
    '/internal-information-update': [{ label: t('internalInformationUpdate'), path: '/internal-information-update' }],

    // Landing routes that previously fell through to "Home".
    '/leads': [{ label: t('leads'), path: '/leads' }],
    '/accounting/agents': [{ label: t('agent'), path: '/accounting/agents' }],
    '/accounting/credits': [{ label: t('credits'), path: '/accounting/credits' }],
    '/accounting/instalments': [{ label: t('instalments'), path: '/accounting/instalments' }],
    '/accounting/insurer-tables': [{ label: t('insurerTables'), path: '/accounting/insurer-tables' }],
    '/ir-settings': [{ label: t('irSettings'), path: '/ir-settings' }],
    '/promotion-admin': [{ label: t('promotionAdmin'), path: '/promotion-admin' }],
    '/promotion-admin/campaign-promotion': [
      { label: t('campaignPromotion'), path: '/promotion-admin/campaign-promotion' },
    ],
    '/commission-and-promotions': [{ label: t('commissionPromotions'), path: '/commission-and-promotions' }],
    '/manage-staff': [{ label: t('manageStaff'), path: '/manage-staff' }],
    '/banner-upload': [{ label: t('bannerUpload'), path: '/banner-upload' }],
    '/notification-admin': [{ label: t('notificationsAdmin'), path: '/notification-admin' }],
    '/chatwoot-payments': [{ label: t('chatwootPayments'), path: '/chatwoot-payments' }],
    '/shortened-url': [{ label: t('shortenedUrl'), path: '/shortened-url' }],
    '/downline': [{ label: t('downline'), path: '/downline' }],
    '/ops-dashboard': [{ label: t('opsDashboard'), path: '/ops-dashboard' }],
    '/get-quote/bike-insurance': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('bikeInsurance'), path: `${buildUrlWithParams('/get-quote/bike-insurance')}` },
    ],
    '/get-quote/bike-custom-quote': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('bikeCustomQuote'), path: `${buildUrlWithParams('/get-quote/bike-custom-quote')}` },
    ],
    '/non-motor-declaration': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('nonMotor'), path: `${buildUrlWithParams('/get-quote/non-motor')}` },
      { label: t('nonMotorDeclaration'), path: `${buildUrlWithParams('/non-motor-declaration')}` },
    ],
    '/non-motor-postquote': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('nonMotor'), path: `${buildUrlWithParams('/get-quote/non-motor')}` },
      { label: t('nonMotorPostquote'), path: `${buildUrlWithParams('/non-motor-postquote')}` },
    ],
    '/non-motor-payment': [
      { label: t('createLeads'), path: '/create-leads' },
      { label: t('nonMotor'), path: `${buildUrlWithParams('/get-quote/non-motor')}` },
      { label: t('nonMotorPayment'), path: `${buildUrlWithParams('/non-motor-payment')}` },
    ],
  };

  // Get breadcrumb items for current path
  const getBreadcrumbItems = () => {
    // Check for exact match first
    if (breadcrumbConfig[pathname]) {
      return breadcrumbConfig[pathname];
    }

    // Check for wildcard patterns (keys ending with /*)
    for (const [pattern, breadcrumbs] of Object.entries(breadcrumbConfig)) {
      if (pattern.endsWith('/*')) {
        const prefix = pattern.slice(0, -2); // Remove '/*' from the end
        if (pathname.startsWith(prefix)) {
          return breadcrumbs;
        }
      }
    }

    return [{ label: t('home'), path: '/' }];
  };

  const breadcrumbOverride = useBreadcrumbStore((state) => state.override);
  const breadcrumbItems = breadcrumbOverride ?? getBreadcrumbItems();
  const isVoluntaryInsurance = pathname.startsWith('/voluntary-insurance');

  return (
    <header className="sticky top-0 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_1px_2px_0_rgba(0,0,0,0.03)] border-b border-border transition-all duration-200 flex items-center justify-between px-6 h-16">
      <div className="flex items-center gap-3">
        <button
          className="rounded-md hover:bg-accent"
          onClick={toggleSidebar}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <PanelLeft className="h-5 w-5" />
        </button>
        <div className="h-5 w-px bg-border" />

        <Breadcrumb className="pb-0">
          <BreadcrumbList>
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={`${index}-${item.path}`}>
                <BreadcrumbItem>
                  {item.path ? (
                    <BreadcrumbLink
                      asChild
                      className={
                        index === breadcrumbItems.length - 1
                          ? 'text-foreground hover:text-primary'
                          : 'text-muted-foreground hover:text-primary'
                      }
                    >
                      <Link to={item.path}>{item.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <span className="text-foreground">{item.label}</span>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbItems.length - 1 && (
                  <BreadcrumbSeparator className="text-muted-foreground">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-3">
        {/* Global filters — Created On + Owner (only for Voluntary Insurance) */}
        {isVoluntaryInsurance && <VoluntaryInsuranceGlobalFilters />}
        {isFullscreenSupported && (
          <button
            type="button"
            onClick={handleFullscreenClick}
            aria-pressed={isFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="inline-flex items-center justify-center rounded-md p-2 text-foreground transition-colors hover:bg-accent"
          >
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
        )}
      </div>
    </header>
  );
}

/**
 * Global filters for Voluntary Insurance routes.
 * Rendered inside the app header bar (right-aligned).
 * Syncs with usePipelineStore so all tabs share the same filters.
 *
 * Figma: Card-based filter pills — label (gray) | separator | value (orange) + chevron + clear
 */
function VoluntaryInsuranceGlobalFilters() {
  const { t, locale } = useTranslation({ en: sharedEn, th: sharedTh });
  const dateRange = usePipelineStore((s) => s.dateRange);
  const ownerFilter = usePipelineStore((s) => s.ownerFilter);
  const setDateRange = usePipelineStore((s) => s.setDateRange);
  const setOwnerFilter = usePipelineStore((s) => s.setOwnerFilter);
  const [ownerOpen, setOwnerOpen] = React.useState(false);

  const dateFilter = {
    created_at__gte: dateRange.from,
    created_at__lte: dateRange.to,
  };

  // Owner is multi-select; the store holds the full array of selected ids,
  // sent to the API as the `owner` array and used for My Case detection.
  const selectedOwnerIds = ownerFilter;

  // Fetch real staff list (cached 10 min). Falls back to empty list while loading or on error.
  const { data: staffList = [] } = useStaffList();
  // "Name · Team" per Figma (FTT-15988); multi-team staff list all teams.
  // toStaffOption also guarantees a non-empty string search value — a staff
  // record without a fullname used to feed cmdk `value={null}` and crash the page.
  const ownerOptions = staffList.map(toStaffOption);

  const handleDateChange = (updates: { created_at__gte?: string; created_at__lte?: string }) => {
    const merged = { ...dateFilter, ...updates };
    // DateRangeFilter commits `undefined` for both ends when the selected start day is
    // clicked again (react-day-picker deselects) and the popover then closes. Stored as-is,
    // that emptied the pipeline range in memory: every list and count request until the
    // next reload went out with no created_at bounds (a full walk of the sales table,
    // 2026-09-18, 44.7 s), and the clear × never rendered because there was no range to
    // clear. An empty commit means "back to the default window", the same thing the ×
    // and the empty-state button do.
    if (!merged.created_at__gte && !merged.created_at__lte) {
      setDateRange(getDefaultDateRange());
      return;
    }
    setDateRange({
      from: merged.created_at__gte,
      to: merged.created_at__lte,
    });
  };

  // Multi-select toggle: add the id if absent, remove it if present. The
  // popover stays open so multiple owners can be picked in one session.
  const handleToggleOwner = (id: number) => {
    setOwnerFilter(ownerFilter.includes(id) ? ownerFilter.filter((x) => x !== id) : [...ownerFilter, id]);
  };

  // Day + short month only ("4 Aug" / "4 ส.ค.") — no year, so the Thai Buddhist era never shows up.
  const formatDisplayDate = (iso?: string) =>
    iso ? formatDate(iso, locale === 'th' ? 'th-TH' : 'en-GB', { day: 'numeric', month: 'short' }) : '--';

  const dateDisplayText = `${formatDisplayDate(dateRange.from)} ${t('dateRangeTo')} ${formatDisplayDate(dateRange.to)}`;
  // Shared with the persist layer so "is this still the untouched default?"
  // has one definition — the × clears to exactly what isn't persisted.
  const hasDateFilter = (dateRange.from || dateRange.to) && !isDefaultDateRange(dateRange);

  const handleClearDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDateRange(getDefaultDateRange());
  };

  // Selected owners surface as removable chips in each page's filter bar
  // (OwnerFilterChips), not here — the header only hosts the picker.
  // Empty state per FTT-15414: just "Owner ⌄" (no separator / value text).
  const hasOwnerFilter = selectedOwnerIds.length > 0;
  const ownerDisplayText = hasOwnerFilter ? t('ownerSelectedCount', { count: selectedOwnerIds.length }) : null;

  const handleClearOwner = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOwnerFilter([]);
  };

  return (
    <div className="flex items-center gap-2">
      {/* Created On — Date Range Picker (Card-based per Figma) */}
      <DateRangeFilter
        filter={dateFilter}
        onFilterChange={handleDateChange}
        startDateKey="created_at__gte"
        endDateKey="created_at__lte"
        maxRangeMonths={MAX_DATE_RANGE_MONTHS}
        renderTrigger={({ onClick }) => (
          <div
            className="flex items-center border rounded-lg bg-background overflow-hidden cursor-pointer pl-3"
            onClick={onClick}
          >
            <span className="text-sm text-muted-foreground whitespace-nowrap">{t('createdOn')}</span>
            <Separator orientation="vertical" className="mx-2 h-5" />
            {/* pr-3 mirrors the Owner trigger's px-3 so both chips share the same
                right edge; the optional clear × below sits after it like Owner's. */}
            <div className="flex items-center gap-1 py-2 pr-3">
              <span className="text-sm font-medium text-primary whitespace-nowrap">{dateDisplayText}</span>
              <ChevronDown className="h-4 w-4 text-primary shrink-0" />
            </div>
            {hasDateFilter && (
              <button className="flex items-center justify-center px-2 py-2 hover:bg-accent" onClick={handleClearDate}>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
        )}
      />

      {/* Owner — Multi-Select combobox (Card-based per Figma) */}
      <div className="flex items-center border rounded-lg bg-background overflow-hidden">
        <Popover open={ownerOpen} onOpenChange={setOwnerOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="flex items-center gap-1 px-3 py-2 h-9 focus:outline-none">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('owner')}</span>
              {hasOwnerFilter && (
                <>
                  <Separator orientation="vertical" className="mx-1 h-5" />
                  <span className="text-sm font-medium text-primary whitespace-nowrap max-w-[120px] truncate">
                    {ownerDisplayText}
                  </span>
                </>
              )}
              <ChevronDown
                className={`h-4 w-4 shrink-0 ${hasOwnerFilter ? 'text-primary' : 'text-muted-foreground'}`}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('ownerSearchPlaceholder')} />
              <CommandList>
                <CommandEmpty>{t('ownerNoResults')}</CommandEmpty>
                <CommandGroup>
                  {ownerOptions.map((opt) => {
                    const isSelected = selectedOwnerIds.includes(Number(opt.value));
                    return (
                      <CommandItem
                        key={opt.value}
                        // Team is baked into searchValue so cmdk matches on it too.
                        value={opt.searchValue}
                        onSelect={() => handleToggleOwner(Number(opt.value))}
                      >
                        <Check className={`mr-2 h-4 w-4 shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="truncate">{opt.label}</span>
                        {opt.team && <span className="ml-1 text-xs text-muted-foreground truncate">· {opt.team}</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {hasOwnerFilter && (
          <button
            className="flex items-center justify-center px-2 py-2 hover:bg-accent shrink-0"
            onClick={handleClearOwner}
            aria-label={t('ownerClearAll')}
          >
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}

export default AuthenticatedHeader;
